import { useGetProcessQuery } from "@/services/backend/processes/process/service";
import ConfirmationDialog from "@/shared/components/domain/confirmation/confirmation-dialog";
import { RTKQueryErrorAlert } from "@/shared/components/domain/errors/rtk-query-error-alert";
import {
  useErrArtefactNotReady,
  useSetErrArtefactNotReadyFromRtk,
} from "@/shared/service-manager/artefact/err-artefact-not-ready";
import { Button } from "@/shared/components/ui/button";
import { ReactElement, useEffect } from "react";
import { v4 } from "uuid";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { useNavigate } from "react-router";
import { useCreateNewInspectionFromCountingMutation } from "@/services/backend/inspections/service";

interface CreateInspectionButtonProps {
  processId: string;
  countingId: string;
  disabled?: boolean;
}

export default function CreateInspectionModal({
  processId,
  countingId,
  disabled,
}: CreateInspectionButtonProps): ReactElement {
  const { user } = useAuth();
  const nav = useNavigate();
  const [createInspection, { data, error, reset, isLoading, isSuccess }] =
    useCreateNewInspectionFromCountingMutation();
  const { resetNotReadyError } = useErrArtefactNotReady();
  useSetErrArtefactNotReadyFromRtk(error, () => {
    reset();
  });

  useEffect(() => {
    if (isSuccess && data != null && data.inspection != null) {
      nav(`/processes/${processId}/inspection/${data.inspection.id}/results`);
    }
  }, [isSuccess, data, nav, processId]);

  const {
    data: process,
    isLoading: processIsLoading,
    error: processError,
  } = useGetProcessQuery({
    id: processId ?? "",
  });

  if (processIsLoading) {
    return <div />;
  }

  if (processError) {
    return <RTKQueryErrorAlert error={error} />;
  }

  const onConfirm = () => {
    if (isLoading) {
      return;
    }

    if (!process) {
      return;
    }

    createInspection({
      id: v4(),
      processId,
      countingId,
      createdBy: user!.id,
    });

    resetNotReadyError();
  };

  return (
    <ConfirmationDialog
      trigger={<Button disabled={disabled}>Bericht erstellen</Button>}
      title="Bericht erstellen"
      description="Hiermit wird der Bericht erstellt."
      onConfirm={onConfirm}
    />
  );
}
